import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import {BaseSeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import {miniSpacingLeft} from 'styles/Utilities';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {
  StudySingleProcedureRunAnalysisQuery,
  StudySingleProcedureRunAnalysisQueryVariables,
} from './__generated/StudySingleProcedureRunAnalysisQuery';

export type SingleProcedureRunSeedDataConfig = BaseSeedDataConfig<
  SeedDataConfigType.PROCEDURE_RUN,
  {procedureRunId: string}
>;

export function validateSingleProcedureRunSeedDataConfigValid(
  config: SingleProcedureRunSeedDataConfig
): boolean {
  // None of the fields in SingleProcedureRunSeedDataConfig are editable, so the config is always valid.
  return true;
}

export function SingleProcedureRunSeedDataConfigEditor(props: {
  value: SingleProcedureRunSeedDataConfig;
  onChange: (value: SingleProcedureRunSeedDataConfig) => void;
}): JSX.Element {
  const result = useBenchlingQuery<
    StudySingleProcedureRunAnalysisQuery,
    StudySingleProcedureRunAnalysisQueryVariables
  >(PMEI_QUERY, {variables: {id: props.value.data.procedureRunId}, renderCachedDataWhenRefetching: true});
  return (
    <FormGroup>
      <FieldLabel label="Selected unit operations" />
      <HelpButton
        renderContent={() => (
          <p className={css(styles.helpToolTipText)}>
            Total instances is taken from the number of unit operations, conditions, and replicates.
          </p>
        )}
        iconClassName={css(styles.helpToolTip)}
        interaction={FloatingInteraction.HOVER}
      />
      {(() => {
        if (result.type === QueryResultType.PENDING || result.type === QueryResultType.ERROR) {
          return <CenteredHorizontalThrobber />;
        }
        if (isInaccessible(result.data.procedureRun)) {
          return (
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="This run is no longer accessible. Try refreshing the page if this is unexpected."
            />
          );
        }
        return (
          <Row className={css(styles.instancesText)}>
            {result.data.procedureRun.methodExecutionInstances.length} instances
          </Row>
        );
      })()}
    </FormGroup>
  );
}

export const PMEI_QUERY: DocumentNode = gql`
  query StudySingleProcedureRunAnalysisQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $id) {
      ... on ProcedureRunNode {
        id
        methodExecutionInstances {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const styles = StyleSheet.create({
  helpToolTip: miniSpacingLeft,
  helpToolTipText: {marginTop: '5px', marginLeft: '25px'},
  instancesText: {marginLeft: '3px'},
});
